import { BlockReference } from '@/cms/types/block';

import { isBuildPhase } from '@/constant/env';
import { trackCacheHit } from '@/utils/fetch';

import { Header } from './Header';
import { HeaderStrapiProps, transformStrapiHeader } from './transformer';
import { fetchHeaderData } from './utils';

// Build-time cache for headers to prevent duplicate fetches
const buildHeaderCache = new Map<string, HeaderStrapiProps>();

const getCachedHeaderData = async (
  documentId: string
): Promise<HeaderStrapiProps> => {
  // During build, check cache first
  if (isBuildPhase && buildHeaderCache.has(documentId)) {
    trackCacheHit();
    return buildHeaderCache.get(documentId)!;
  }

  const headerData = await fetchHeaderData(documentId);

  const data = headerData?.data?.[0]?.attributes;

  if (!data) {
    throw new Error(`Header with ID ${documentId} not found`);
  }

  // Cache during build
  if (isBuildPhase) {
    buildHeaderCache.set(documentId, data);
  }

  return data;
};

/**
 * Mainly for backward compatibility with the old HeaderStrapi component
 *
 * if attributes are present, use them, otherwise fetch / cache the data for the header
 * using the id. More details to be added
 */
const discriminatorlyGetProps = async ({ block }: HeaderStrapiPropsBlock) => {
  let data = undefined as HeaderStrapiProps | undefined;
  if (!block.data) return null;

  // treat logo as a switch case
  if (
    'attributes' in block.data &&
    block.data.attributes &&
    block.data.attributes.logo
  ) {
    data = block.data.attributes as HeaderStrapiProps;
  } else {
    if (!block.data.documentId) {
      throw new Error('Document ID is required');
    }

    data = await getCachedHeaderData(block.data.documentId);
  }
  return data;
};

type HeaderStrapiPropsBlock = {
  block: BlockReference<HeaderStrapiProps>;
};

export async function HeaderStrapi({ block }: HeaderStrapiPropsBlock) {
  const data = await discriminatorlyGetProps({ block });

  if (!data) return null;

  return <Header {...transformStrapiHeader(data)} />;
}
